import { useCallback, useEffect, useRef, useState } from 'react';

import { uploadLogToS3 } from '../app-logs/app-logs.utils';
import { electronApis } from '../electron-api';

import { type FeedbackType } from './feedback.types';

interface UseTodayLogsOptions {
  log?: string;
  feedbackType: FeedbackType;
}

type LogFile = {
  content: string;
  fileName: string;
};

export function useTodayLogs({ log, feedbackType }: UseTodayLogsOptions) {
  const logsRef = useRef<LogFile[]>([]);
  const hasFetchedRef = useRef(false);
  const [logsCount, setLogsCount] = useState(0);
  const [isUploadingLogs, setIsUploading] = useState(false);

  useEffect(() => {
    // Only fetch if bug type, no log prop, and haven't fetched yet
    if (log !== undefined || feedbackType !== 'bug' || hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;

    const fetchLatestLogs = async () => {
      try {
        const latestLogFile =
          await electronApis.system.getLatestLogFileContent();
        const latestCrashLog =
          await electronApis.system.getLatestCrashLogContent();
        const latestContextKitCliCrashLog =
          await electronApis.system.getLatestContextKitCliCrashLogContent();

        const todayLogs = [
          latestLogFile,
          latestCrashLog,
          latestContextKitCliCrashLog,
        ].filter(Boolean) as LogFile[];
        logsRef.current = todayLogs;
        setLogsCount(todayLogs.length);
      } catch (err) {
        console.error('Failed to fetch logs:', err);
        logsRef.current = [];
        setLogsCount(0);
      }
    };

    fetchLatestLogs();
  }, [log, feedbackType]);

  const uploadLogs = useCallback(async (): Promise<string[]> => {
    const logs = logsRef.current;
    if (logs.length === 0) return [];

    setIsUploading(true);

    try {
      const uploadedUrls = await Promise.all(
        logs.map(async (logFile) => {
          const url = await uploadLogToS3(logFile.content, logFile.fileName);
          return url;
        })
      );
      return uploadedUrls;
    } catch (err) {
      console.error('Failed to upload logs:', err);
      return [];
    } finally {
      setIsUploading(false);
    }
  }, []);

  return {
    uploadLogs,
    logsCount,
    shouldUploadLogs: log === undefined && feedbackType === 'bug',
    isUploading: isUploadingLogs,
  };
}
